/* Atelier Tenant Home — boutique salon, intimate, hand-made.

   Emotional intent: TENDERNESS. A reader feels like they've walked into a
   small Parisian portrait studio behind a velvet curtain. Generous
   whitespace, narrow centered column, italic everything, photos framed
   like keepsakes. NOTHING is full-bleed — every image sits inside the
   column with rose-gold inner borders. Contrast intentionally low.

   Structurally distinct from Editorial:
   • Single centered column (max-w-3xl) for ALL content — no wide grids.
   • Hero is a small portrait + caption below + italic headline below
     that — feels like a calling card, not a cinema poster.
   • Services rendered as a stacked single-column list with hairline rules
     between (not a 3-col grid).
   • About is a pull-quote with massive italic body text.
   • Closing signature stamp "With care · Studio".
*/

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { tenantHref } from "@/lib/tenant";
import { optimizeImageUrl } from "@/lib/imageOptim";
import { EditorialSkeleton } from "../editorial/_EditorialSkeleton";

const FALLBACK_HERO = "https://images.unsplash.com/photo-1532073150508-0c1df022bdd1?auto=format&fit=crop&w=1600&q=80";

export default function AtelierTenantHome() {
  const { settings, loading } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const heroImg = settings?.hero_image || settings?.portfolio_images?.[0] || FALLBACK_HERO;
  const heroPos = `${settings?.hero_focal_x ?? 50}% ${settings?.hero_focal_y ?? 50}%`;
  const headline = settings?.hero_title_line1 || "By appointment.";
  const subhead = settings?.hero_title_line2 || "By heart.";
  const intro = settings?.hero_description || `${studioName} is a small portrait atelier — a quiet room, kind light, and a slow conversation that ends in pictures you'll keep.`;
  const aboutTitle = settings?.about_title || "Made in a small room, by hand.";
  const aboutBody = settings?.about_body || "Each sitting is a single session, planned around you. Three to four soft setups, one cup of tea, and a closed door. You'll leave with images that feel like you, not like Instagram.";
  const portfolio = (settings?.portfolio_images || []).slice(0, 4);

  const [services, setServices] = useState([]);
  useEffect(() => {
    api.get("/services").then((r) => setServices(Array.isArray(r.data) ? r.data.slice(0, 3) : [])).catch(() => {});
  }, []);

  useEffect(() => { document.title = `${studioName} — Atelier`; }, [studioName]);

  if (loading && !settings) return <EditorialSkeleton eyebrow="Drawing the curtain." />;

  return (
    <div data-testid="atelier-tenant-home"
      style={{ background: "var(--tenant-bg, #FEF5F2)", color: "var(--tenant-fg, #3A1B22)" }}>

      {/* ── HERO — calling-card layout, no full-bleed ────────────────── */}
      <section className="px-6 lg:px-12 pt-20 pb-24" data-testid="atelier-hero">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-[10px] uppercase tracking-[0.42em] mb-12" style={{ color: "var(--tenant-accent)" }}>
            By appointment · One sitting at a time
          </div>
          {/* Small rounded portrait — NOT full-bleed */}
          <div className="relative inline-block mb-12">
            <div className="absolute -inset-3 rounded-[28px] border opacity-50"
                 style={{ borderColor: "var(--tenant-accent)" }} aria-hidden />
            <img src={optimizeImageUrl(heroImg, { w: 900 })}
              alt={`${studioName} sitting`}
              className="relative w-[320px] h-[420px] sm:w-[420px] sm:h-[540px] object-cover rounded-[20px]"
              style={{ objectPosition: heroPos }}
              loading="eager" decoding="async" />
          </div>
          <h1 className="leading-[1.05]" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2.5rem, 6vw, 5rem)" }}>
            <span className="italic">{headline}</span>
            <br />
            <span className="italic" style={{ color: "var(--tenant-accent)" }}>{subhead}</span>
          </h1>
          <p className="mt-10 text-base sm:text-lg leading-relaxed max-w-xl mx-auto italic" style={{ color: "var(--tenant-fg-soft)" }}>
            {intro}
          </p>
          <div className="mt-12">
            <Link to={tenantHref("/booking")} data-testid="atelier-hero-book"
              className="inline-flex items-center gap-3 px-9 py-4 text-[11px] uppercase tracking-[0.32em] rounded-full border-2 transition-all hover:scale-[1.02]"
              style={{ borderColor: "var(--tenant-accent)", color: "var(--tenant-accent)" }}>
              Reserve a sitting →
            </Link>
          </div>
        </div>
      </section>

      {/* ── PULL-QUOTE ABOUT — massive italic body text ──────────────── */}
      <section className="px-6 lg:px-12 py-24 border-y" style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 25%, transparent)" }}>
        <div className="max-w-2xl mx-auto text-center">
          <div className="text-3xl leading-[0.9] mb-8" style={{ color: "var(--tenant-accent)", fontFamily: "var(--tenant-serif)" }}>“</div>
          <h2 className="italic leading-tight" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(1.5rem, 3vw, 2.5rem)" }}>
            {aboutTitle}
          </h2>
          <p className="mt-8 text-base leading-loose max-w-lg mx-auto" style={{ color: "var(--tenant-fg-soft)" }}>
            {aboutBody}
          </p>
        </div>
      </section>

      {/* ── SERVICES — single-column stack with hairlines ────────────── */}
      {services.length > 0 && (
        <section className="px-6 lg:px-12 py-24" data-testid="atelier-services">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-14">
              <div className="text-[10px] uppercase tracking-[0.42em] mb-3" style={{ color: "var(--tenant-accent)" }}>
                The sittings
              </div>
              <h2 className="italic" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)" }}>
                Three ways to be seen.
              </h2>
            </div>
            <ul>
              {services.map((svc) => (
                <li key={svc.id} className="py-8 border-t flex items-baseline justify-between gap-6"
                    style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 25%, transparent)" }}>
                  <div className="flex-1">
                    <div className="italic text-2xl mb-1" style={{ fontFamily: "var(--tenant-serif)" }}>{svc.name}</div>
                    {svc.short_description && (
                      <div className="text-sm leading-relaxed" style={{ color: "var(--tenant-fg-soft)" }}>{svc.short_description}</div>
                    )}
                  </div>
                  <Link to={tenantHref(`/services#${svc.id || svc.slug || ""}`)}
                    className="text-[10px] uppercase tracking-[0.32em] whitespace-nowrap pb-0.5 border-b transition-opacity hover:opacity-80"
                    style={{ color: "var(--tenant-accent)", borderColor: "var(--tenant-accent)" }}>
                    Read →
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* ── PORTFOLIO TEASER — single-column rounded ────────────────── */}
      {portfolio.length > 0 && (
        <section className="px-6 lg:px-12 py-24">
          <div className="max-w-2xl mx-auto space-y-10">
            {portfolio.slice(0, 3).map((src, i) => (
              <figure key={i}>
                <img src={optimizeImageUrl(src, { w: 1400 })} alt=""
                  className="w-full aspect-[4/5] object-cover rounded-[16px]"
                  loading="lazy" decoding="async" />
              </figure>
            ))}
            <div className="text-center pt-4">
              <Link to={tenantHref("/portfolio")}
                className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.42em] pb-0.5 border-b"
                style={{ color: "var(--tenant-accent)", borderColor: "var(--tenant-accent)" }}>
                View the full archive →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── SIGNATURE STAMP CLOSING ───────────────────────────────────── */}
      <section className="px-6 py-24 text-center">
        <div className="text-[10px] uppercase tracking-[0.5em] mb-3" style={{ color: "var(--tenant-fg-mute)" }}>With care</div>
        <div className="text-2xl italic" style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-accent)" }}>
          — {studioName}
        </div>
      </section>
    </div>
  );
}
